import React, { useState } from 'react';
import { siteConfig } from '../data';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const Contact = () => {
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('Tusind tak for din besked. Jeg vender tilbage til dig snarest muligt.');
        e.target.reset();
      } else {
        setStatus('Der opstod en fejl. Prøv venligst igen eller kontakt mig direkte via mail.');
      }
    } catch (error) {
      setStatus('Der opstod en fejl. Prøv venligst igen eller kontakt mig direkte via mail.');
    }
  };

  return (
    <section id="kontakt" className="pt-24 pb-24 md:pt-32 md:pb-32 bg-stone-50/30">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {/* Info Side */}
          <div className="lg:col-span-5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-6 block font-bold">
              Kontakt
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-stone-900 mb-10 leading-tight">
              Jeg glæder mig til at <br />
              <span className="font-serif italic text-stone-500 lowercase tracking-normal">byde dig velkommen</span>
            </h2>
            
            <p className="text-stone-600 font-light text-lg leading-relaxed mb-12 max-w-sm">
              Har du spørgsmål til behandlingen? Du er altid velkommen til at kontakte mig.
            </p>

            <div className="space-y-8">
              <a href={`mailto:${siteConfig.email}`} className="flex items-center space-x-6 group">
                <div className="w-12 h-12 bg-white flex items-center justify-center group-hover:bg-stone-900 group-hover:text-stone-50 transition-all duration-500 border border-stone-200 shadow-sm">
                  <Mail size={18} strokeWidth={1.5} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Email</span>
                  <span className="text-stone-900 font-light text-sm border-b border-transparent group-hover:border-stone-900 transition-all">{siteConfig.email}</span>
                </div>
              </a>
              <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="flex items-center space-x-6 group">
                <div className="w-12 h-12 bg-white flex items-center justify-center group-hover:bg-stone-900 group-hover:text-stone-50 transition-all duration-500 border border-stone-200 shadow-sm">
                  <Phone size={18} strokeWidth={1.5} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Telefon</span>
                  <span className="text-stone-900 font-light text-sm border-b border-transparent group-hover:border-stone-900 transition-all">{siteConfig.phone}</span>
                </div>
              </a>
              <div className="flex items-center space-x-6 group">
                <div className="w-12 h-12 bg-white flex items-center justify-center border border-stone-200 shadow-sm">
                  <MapPin size={18} strokeWidth={1.5} className="text-stone-400" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Klinik</span>
                  <span className="text-stone-900 font-light text-sm">{siteConfig.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div className="lg:col-span-7 bg-orange-50/30 p-8 md:p-12 shadow-sm border border-stone-100">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - ABSOLUTELY HIDDEN */}
              <div className="sr-only absolute opacity-0 -z-10 select-none pointer-events-none">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <label htmlFor="name" className="text-[10px] uppercase tracking-widest text-stone-500">Navn</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="Dit fulde navn"
                    className="w-full border-b border-stone-200 py-2 px-0 focus:outline-none focus:border-stone-800 transition-colors bg-transparent font-normal text-sm text-stone-800 placeholder:text-stone-500"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="email" className="text-[10px] uppercase tracking-widest text-stone-500">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="din@email.dk"
                    className="w-full border-b border-stone-200 py-2 px-0 focus:outline-none focus:border-stone-800 transition-colors bg-transparent font-normal text-sm text-stone-800 placeholder:text-stone-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="message" className="text-[10px] uppercase tracking-widest text-stone-500">Besked</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="3"
                  placeholder="Hvordan kan jeg hjælpe dig?"
                  className="w-full border-b border-stone-200 py-2 px-0 focus:outline-none focus:border-stone-800 transition-colors bg-transparent font-normal text-sm text-stone-800 resize-none placeholder:text-stone-500"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-[#b8a58d] text-stone-50 px-6 py-3 text-xs tracking-widest uppercase hover:bg-[#a6947e] transition-all duration-300 flex items-center justify-center group"
              >
                Send besked
                <Send size={14} className="ml-2 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>

              {status && (
                <div className={`mt-4 p-3 text-xs font-light rounded ${status.includes('fejl') ? 'text-red-500 bg-red-50' : 'text-stone-600 bg-stone-50'}`}>
                  {status}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
